/* eslint-disable linebreak-style */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  colors,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import Loader from 'react-loader-spinner';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { strapi } from '../../../../strapi/strapi.config';
import { selectCurrentUser } from '../../../../redux/user/user.selectors';
import { t } from '../../../../i18n';

const useStyles = makeStyles(() => ({
  header: {
    background: colors.blueGrey[400]
  },
  button: {
    background: colors.blueGrey[500]
  }
}));

const pdfStyles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9
  },
  title: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: 700
  },
  subtitle: {
    marginBottom: 10
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    borderBottomStyle: 'solid',
    paddingVertical: 4
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    borderBottomStyle: 'solid',
    backgroundColor: '#f0f0f0',
    paddingVertical: 4,
    fontWeight: 700
  },
  group: {
    marginBottom: 14
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: '#1a237e'
  },
  totalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#222',
    borderTopStyle: 'solid',
    backgroundColor: '#e8eaf6',
    paddingVertical: 4,
    fontWeight: 700
  },
  grandGroup: {
    marginTop: 10
  },
  grandTotalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#222',
    borderStyle: 'solid',
    backgroundColor: '#c5cae9',
    paddingVertical: 5,
    fontWeight: 700
  },
  cTip: { width: '14%' },
  cId: { width: '10%' },
  cData: { width: '12%' },
  cClient: { width: '22%' },
  cVal: { width: '13%', textAlign: 'right' },
  cObs: { width: '16%', paddingLeft: 4 },
  cObsValue: { width: '16%', fontSize: 7, paddingLeft: 4 }
});

const pad2 = (v) => String(v).padStart(2, '0');

const formatDateRO = (value) => {
  if (!value) {
    return '-';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const getTodayRO = () => {
  const d = new Date();
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const getMonthStartRO = () => {
  const d = new Date();
  return `${pad2(1)}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const parseRoDate = (value, endOfDay = false) => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value || '').trim());
  if (!match) {
    return null;
  }
  const zi = Number(match[1]);
  const luna = Number(match[2]);
  const an = Number(match[3]);
  const d = new Date(an, luna - 1, zi, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  if (d.getFullYear() !== an || d.getMonth() !== luna - 1 || d.getDate() !== zi) {
    return null;
  }
  return d;
};

const formatNumber = (value) => Number(value || 0).toFixed(2);

const EURO_TIPS = ['RE', 'UG', 'PI'];

const monedaForTip = (tip) => (EURO_TIPS.includes(String(tip || '').toUpperCase()) ? 'EUR' : 'LEI');

const formatMoney = (value, moneda) => `${Number(value || 0).toFixed(2)} ${moneda}`;

const emptyTotals = () => ({
  valoareComanda: 0,
  valoareClientFinal: 0
});

const addTotals = (acc, row) => ({
  valoareComanda: acc.valoareComanda + Number(row.valoareComanda || 0),
  valoareClientFinal: acc.valoareClientFinal + Number(row.valoareClientFinal || 0)
});

const tipProdusLabel = (order) => {
  const tip = String(order.tipProdus || '').toUpperCase();
  const sub = String(order.subTip || '').toUpperCase();
  return sub ? `${tip}-${sub}` : tip || '-';
};

const buildRows = (orders) => {
  return (orders || []).map(order => {
    const tip = String(order.tipProdus || '').toUpperCase().trim()
      || tipProdusLabel(order).slice(0, 2).toUpperCase();
    return {
      tip,
      tipProdus: tipProdusLabel(order),
      id: order.id,
      data: formatDateRO(order.dataComanda),
      client: order.clientClient || '-',
      valoareComanda: Number(order.valoareComanda || 0),
      valoareClientFinal: Number(order.valoareClientFinal || 0),
      observatii: String(order.observatii || '').trim() || '-'
    };
  });
};

const groupRowsByTipProdus = (rows) => {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const key = row.tip || (row.tipProdus || '-').slice(0, 2).toUpperCase();
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(row);
  });
  return Array.from(groups.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([tipProdus, groupRows]) => {
      const sortedRows = [...groupRows].sort((a, b) => Number(a.id) - Number(b.id));
      const totals = sortedRows.reduce(addTotals, emptyTotals());
      return { tipProdus, moneda: monedaForTip(tipProdus), rows: sortedRows, totals };
    });
};

const computeGrandTotals = (groups) => {
  const result = { LEI: emptyTotals(), EUR: emptyTotals() };
  groups.forEach((group) => {
    result[group.moneda] = addTotals(result[group.moneda], group.totals);
  });
  return result;
};

const TableHeader = () => (
  <View style={pdfStyles.headerRow}>
    <Text style={pdfStyles.cTip}>{t('reports.user.columns.productType')}</Text>
    <Text style={pdfStyles.cId}>{t('reports.user.columns.orderId')}</Text>
    <Text style={pdfStyles.cData}>{t('reports.user.columns.date')}</Text>
    <Text style={pdfStyles.cClient}>{t('reports.user.columns.client')}</Text>
    <Text style={pdfStyles.cVal}>{t('reports.user.columns.orderValue')}</Text>
    <Text style={pdfStyles.cVal}>{t('reports.user.columns.finalValue')}</Text>
    <Text style={pdfStyles.cObs}>{t('reports.user.columns.notes')}</Text>
  </View>
);

const ReportDocument = ({ rows, denumireFirma, dataStart, dataEnd }) => {
  const groups = groupRowsByTipProdus(rows);
  const grandTotals = computeGrandTotals(groups);
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.title}>{t('reports.user.pdfTitle')}</Text>
        <Text style={pdfStyles.subtitle}>
          {t('reports.user.companyPeriod', { name: denumireFirma || '-', start: dataStart, end: dataEnd })}
        </Text>
        <Text style={pdfStyles.subtitle}>{t('reports.common.onlySentOrders')}</Text>

        {groups.map((group) => (
          <View key={group.tipProdus} style={pdfStyles.group}>
            <Text style={pdfStyles.groupTitle}>{t('reports.user.productType', { type: group.tipProdus, currency: group.moneda })}</Text>
            <TableHeader />
            {group.rows.map((row, idx) => (
              <View key={`${row.id}-${idx}`} style={pdfStyles.row}>
                <Text style={pdfStyles.cTip}>{row.tipProdus}</Text>
                <Text style={pdfStyles.cId}>{String(row.id)}</Text>
                <Text style={pdfStyles.cData}>{row.data}</Text>
                <Text style={pdfStyles.cClient}>{row.client}</Text>
                <Text style={pdfStyles.cVal}>{formatNumber(row.valoareComanda)}</Text>
                <Text style={pdfStyles.cVal}>{formatNumber(row.valoareClientFinal)}</Text>
                <Text style={pdfStyles.cObsValue}>{row.observatii}</Text>
              </View>
            ))}
            <View style={pdfStyles.totalRow}>
              <Text style={pdfStyles.cTip}>{t('reports.user.totalByType', { type: group.tipProdus })}</Text>
              <Text style={pdfStyles.cId} />
              <Text style={pdfStyles.cData} />
              <Text style={pdfStyles.cClient} />
              <Text style={pdfStyles.cVal}>{formatMoney(group.totals.valoareComanda, group.moneda)}</Text>
              <Text style={pdfStyles.cVal}>{formatMoney(group.totals.valoareClientFinal, group.moneda)}</Text>
              <Text style={pdfStyles.cObs} />
            </View>
          </View>
        ))}

        <View style={pdfStyles.grandGroup}>
          <Text style={pdfStyles.groupTitle}>{t('reports.user.totalGeneral')}</Text>
          {['LEI', 'EUR'].map((moneda) => (
            <View key={moneda} style={pdfStyles.grandTotalRow}>
              <Text style={pdfStyles.cTip}>{t('reports.user.totalByCurrency', { currency: moneda })}</Text>
              <Text style={pdfStyles.cId} />
              <Text style={pdfStyles.cData} />
              <Text style={pdfStyles.cClient} />
              <Text style={pdfStyles.cVal}>{formatMoney(grandTotals[moneda].valoareComanda, moneda)}</Text>
              <Text style={pdfStyles.cVal}>{formatMoney(grandTotals[moneda].valoareClientFinal, moneda)}</Text>
              <Text style={pdfStyles.cObs} />
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

ReportDocument.propTypes = {
  rows: PropTypes.array,
  denumireFirma: PropTypes.string,
  dataStart: PropTypes.string,
  dataEnd: PropTypes.string
};

const UserReport = ({ currentUser }) => {
  const classes = useStyles();
  const [dataStart, setDataStart] = useState(() => getMonthStartRO());
  const [dataEnd, setDataEnd] = useState(() => getTodayRO());
  const [isGenerating, setIsGenerating] = useState(false);
  const isComisionar = currentUser?.idFirma?.cfg1 === 'DA';
  const idFirma = currentUser?.idFirma?.id;

  if (isComisionar) {
    return null;
  }

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!dataStart || !dataEnd) {
      window.alert(t('reports.common.periodRequired'));
      return;
    }
    const start = parseRoDate(dataStart, false);
    const end = parseRoDate(dataEnd, true);
    if (!start || !end) {
      window.alert(t('reports.common.invalidDateFormat'));
      return;
    }
    if (start > end) {
      window.alert(t('reports.common.startAfterEnd'));
      return;
    }
    if (!idFirma) {
      window.alert(t('reports.user.missingCompany'));
      return;
    }
    setIsGenerating(true);
    try {
      const orders = await strapi.getEntries('comandas', {
        idFirma,
        stadiu: 'TRANSMISA',
        _limit: -1,
        _sort: 'dataComanda:ASC'
      });
      const filtered = (orders || []).filter(order => {
        const d = new Date(order.dataComanda);
        return !Number.isNaN(d.getTime()) && d >= start && d <= end;
      });
      const rows = buildRows(filtered);
      const blob = await pdf(
        <ReportDocument
          rows={rows}
          denumireFirma={currentUser?.idFirma?.denumire || ''}
          dataStart={dataStart}
          dataEnd={dataEnd}
        />
      ).toBlob();
      const fileStart = String(dataStart).replace(/\./g, '-');
      const fileEnd = String(dataEnd).replace(/\./g, '-');
      downloadBlob(blob, `${t('reports.user.filePrefix')}-${fileStart}-${fileEnd}.pdf`);
    } catch (e) {
      window.alert(t('reports.common.generateError', { error: e }));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader className={classes.header} title={t('reports.user.cardTitle')} />
      <Divider />
      <CardContent>
        <div style={{ marginBottom: 8, color: '#455A64', fontWeight: 600 }}>
          {t('reports.common.onlySentOrders')}
        </div>
        <Grid container spacing={2}>
          <Grid item md={2} xs={12}>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={t('reports.common.dateStart')}
              margin="dense"
              onChange={(e) => setDataStart(e.target.value)}
              placeholder={t('reports.common.dateFormatPlaceholder')}
              type="text"
              value={dataStart}
              variant="outlined"
            />
          </Grid>
          <Grid item md={2} xs={12}>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={t('reports.common.dateEnd')}
              margin="dense"
              onChange={(e) => setDataEnd(e.target.value)}
              placeholder={t('reports.common.dateFormatPlaceholder')}
              type="text"
              value={dataEnd}
              variant="outlined"
            />
          </Grid>
          <Grid item md={8} xs={12} style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              className={classes.button}
              color="primary"
              disabled={isGenerating}
              onClick={handleGenerate}
              startIcon={<GetAppIcon />}
              variant="contained"
            >
              {t('reports.user.button')}
            </Button>
            {isGenerating && (
              <div style={{ marginLeft: 12 }}>
                <Loader color="#00BFFF" height={22} type="Puff" width={22} />
              </div>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

UserReport.propTypes = {
  currentUser: PropTypes.object
};

const mapStateToProps = state => ({
  currentUser: selectCurrentUser(state)
});

export default connect(mapStateToProps)(UserReport);
